import { Scene } from "@babylonjs/core";
import { RemotePlayer } from "../player/RemotePlayer.js";
import type { AvatarActivity } from "../player/RemotePlayer.js";

/**
 * Renders the server's simulated shoppers. They are ordinary avatars with an
 * explicit activity, so they reuse RemotePlayer wholesale (same models, same
 * tinting, same blob shadows) rather than growing a parallel avatar path.
 *
 * The server sends an `appearance` number instead of a session id; hashing
 * it into the same string space the players use keeps every shopper's look
 * stable for the life of the room and spread across the variant set.
 */
export class NpcRenderer {
  private readonly scene: Scene;
  private readonly avatars: Map<string, RemotePlayer> = new Map();

  constructor(scene: Scene) {
    this.scene = scene;
  }

  add(id: string, appearance: number, x: number, y: number, z: number): void {
    if (this.avatars.has(id)) {
      return;
    }
    this.avatars.set(id, new RemotePlayer(this.scene, appearanceKey(appearance), x, y, z));
  }

  update(
    id: string,
    x: number,
    y: number,
    z: number,
    rotation: number,
    activity: string,
    hasBag: boolean
  ): void {
    const avatar: RemotePlayer | undefined = this.avatars.get(id);
    if (avatar === undefined) {
      return;
    }
    avatar.setTransform(x, y, z, rotation);
    avatar.setActivity(toActivity(activity));
    avatar.setBag(hasBag);
  }

  remove(id: string): void {
    const avatar: RemotePlayer | undefined = this.avatars.get(id);
    if (avatar === undefined) {
      return;
    }
    avatar.dispose();
    this.avatars.delete(id);
  }

  dispose(): void {
    for (const avatar of this.avatars.values()) {
      avatar.dispose();
    }
    this.avatars.clear();
  }
}

/** Turns the server's appearance number into a stable avatar-key string. */
export function appearanceKey(appearance: number): string {
  return `npc-${appearance.toString(36)}`;
}

/** Unknown activity strings degrade to standing still rather than throwing:
 *  a bad enum on the wire must never take the render loop down. */
export function toActivity(value: string): AvatarActivity {
  if (value === "walk" || value === "browse" || value === "sit") {
    return value;
  }
  console.warn(`[NpcRenderer] unknown activity "${value}", showing idle`);
  return "browse";
}
